/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { CompositionEditorProps } from '../types';
import Typography from '../../../basic/Typography';
import { newsEditorApi } from '../services/newsEditorApi';
import styles from '../NewsEditor.module.css';

const CompositionEditor: React.FC<CompositionEditorProps> = ({
  jobId,
  composition,
  onUpdate,
  onRerun
}) => {
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [selectedOrientation, setSelectedOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [availableVideos, setAvailableVideos] = useState<{portrait: boolean, landscape: boolean}>({portrait: false, landscape: false});
  const [isRerunning, setIsRerunning] = useState(false);
  const [rerunStatus, setRerunStatus] = useState<string>('');
  const [rerunProgress, setRerunProgress] = useState<number>(0);

  // Get video URLs for different orientations
  const getFinalVideoUrl = useCallback((orientation: 'portrait' | 'landscape') => {
    return `/news/out/${jobId}/composition_stage/video_${orientation === 'portrait' ? '720x1280p' : '1280x720p'}.mp4`;
  }, [jobId]);

  const getFinalThumbnailUrl = (orientation: 'portrait' | 'landscape') => {
    // For now, use the video as poster since thumbnails might not exist
    return `/news/out/${jobId}/composition_stage/video_${orientation === 'portrait' ? '720x1280p' : '1280x720p'}.mp4`;
  };

  const handleVideoLoad = () => {
    setIsLoadingVideo(false);
    setVideoError(null);
  };

  const handleVideoError = (event: any) => {
    setIsLoadingVideo(false);
    console.error('Video error details:', event);

    if (!availableVideos[selectedOrientation]) {
      setVideoError(`${selectedOrientation} video is not available for this job.`);
    } else {
      setVideoError(`Failed to load ${selectedOrientation} video. Check console for details.`);
    }
  };

  // Check if video files exist
  const checkVideoExists = async (url: string) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Error checking video existence:', error);
      return false;
    }
  };

    // Debug video URLs and check existence
  useEffect(() => {
    const portraitUrl = getFinalVideoUrl('portrait');
    const landscapeUrl = getFinalVideoUrl('landscape');

    console.log('Portrait video URL:', portraitUrl);
    console.log('Landscape video URL:', landscapeUrl);

    // Check if videos exist and update state
    checkVideoExists(portraitUrl).then(exists => {
      console.log('Portrait video exists:', exists);
      setAvailableVideos(prev => ({ ...prev, portrait: exists }));
      if (!exists) {
        console.warn('Portrait video missing for job:', jobId);
      }
    });

    checkVideoExists(landscapeUrl).then(exists => {
      console.log('Landscape video exists:', exists);
      setAvailableVideos(prev => ({ ...prev, landscape: exists }));
      if (!exists) {
        console.warn('Landscape video missing for job:', jobId);
      }
    });
  }, [jobId]);

  // Auto-switch to available orientation if current one doesn't exist
  useEffect(() => {
    if (availableVideos.portrait && !availableVideos.landscape && selectedOrientation === 'landscape') {
      console.log('Landscape not available, switching to portrait');
      setSelectedOrientation('portrait');
    } else if (availableVideos.landscape && !availableVideos.portrait && selectedOrientation === 'portrait') {
      console.log('Portrait not available, switching to landscape');
      setSelectedOrientation('landscape');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableVideos, selectedOrientation]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = () => {
    const videoUrl = getFinalVideoUrl(selectedOrientation);
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = `final_composition_${selectedOrientation}_${jobId}.mp4`;
    link.click();
  };

  const handleRerun = async () => {
    try {
      setIsRerunning(true);
      setRerunStatus('Starting composition generation...');
      setRerunProgress(10);

      // Trigger composition generation
      await newsEditorApi.triggerComposition(jobId);
      setRerunStatus('Composition generation started successfully!');
      setRerunProgress(50);

      // Start polling for status updates
      const pollInterval = setInterval(async () => {
        try {
          const status = await newsEditorApi.getCompositionStatus(jobId);
          setRerunStatus(`Status: ${status.status}, Progress: ${status.progress}%`);
          setRerunProgress(status.progress || 0);

                    if (status.completed || status.status === 'completed') {
            setRerunStatus('Composition completed! Refreshing videos...');
            setRerunProgress(100);
            clearInterval(pollInterval);

            // Wait a bit for files to be written, then refresh
            setTimeout(() => {
              setIsRerunning(false);
              setRerunStatus('');
              setRerunProgress(0);
              // Trigger a refresh of the video availability
              window.location.reload();
            }, 2000);
          } else if (status.status === 'failed' || status.status === 'error') {
            setRerunStatus('Composition failed. Please try again.');
            setRerunProgress(0);
            clearInterval(pollInterval);
            setTimeout(() => {
              setIsRerunning(false);
              setRerunStatus('');
            }, 3000);
          } else if (status.status === 'in_progress') {
            // Update progress based on artifacts count or status
            const progress = Math.min(status.progress || 50, 95); // Don't go to 100% until completed
            setRerunProgress(progress);
            setRerunStatus(`Composition in progress... ${progress}% (${status.artifacts_count || 0} artifacts)`);
          }
        } catch (error) {
          console.error('Error polling composition status:', error);
          setRerunStatus('Error checking status. Please refresh manually.');
          clearInterval(pollInterval);
          setTimeout(() => {
            setIsRerunning(false);
            setRerunStatus('');
          }, 3000);
        }
      }, 5000); // Poll every 5 seconds

      // Timeout after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isRerunning) {
          setRerunStatus('Composition is taking longer than expected. Please check manually.');
          setIsRerunning(false);
        }
      }, 600000);

    } catch (error) {
      console.error('Error triggering composition:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      if (errorMessage.includes('404')) {
        setRerunStatus('Composition service not available. Please check if the news service is running.');
      } else if (errorMessage.includes('500')) {
        setRerunStatus('Server error occurred. Please try again later.');
      } else {
        setRerunStatus(`Failed to start composition: ${errorMessage}`);
      }

      setIsRerunning(false);
      setTimeout(() => {
        setRerunStatus('');
      }, 5000);
    }
  };

  return (
    <div className={styles['composition-editor']}>
      <div className={styles['editor-header']}>
        <Typography variant="h4">Final Composition</Typography>
        <div className={styles['editor-actions']}>
          <button
            className={styles['composition-rerun-button']}
            onClick={handleRerun}
            disabled={isRerunning}
            title="Generate new composition videos for both portrait and landscape modes"
          >
            {isRerunning ? 'Generating...' : 'Generate Composition'}
          </button>
          <button className={styles['adjust-composition-button']}>Adjust Composition</button>
          <button className={styles['preview-final-button']}>Preview Final</button>
          <button className={styles['export-button']} onClick={handleExport}>Export</button>
        </div>
      </div>

      {/* Rerun Status Display */}
      {isRerunning && (
        <div className={styles['rerun-status-section']}>
          <div className={styles['rerun-progress']}>
            <div className={styles['rerun-progress-bar']}>
              <div
                className={styles['rerun-progress-fill']}
                style={{ width: `${rerunProgress}%` }}
              ></div>
            </div>
            <Typography variant="body2" className={styles['rerun-status-text']}>
              {rerunStatus}
            </Typography>
          </div>
        </div>
      )}

      <div className={styles['composition-status']}>
        <div className={styles['status-info']}>
          <Typography variant="body2" className={styles['status-label']}>Status:</Typography>
          <span className={`${styles['status-value']} ${styles[`status-${composition.status}`]}`}>
            {composition.status}
          </span>
        </div>

        {/* Orientation Selection */}
        <div className={styles['orientation-selector']}>
          <Typography variant="body2" className={styles['orientation-label']}>Select Orientation:</Typography>
          <div className={styles['orientation-buttons']}>
            <button
              className={`${styles['orientation-button']} ${selectedOrientation === 'portrait' ? styles['active'] : ''} ${!availableVideos.portrait ? styles['disabled'] : ''}`}
              onClick={() => setSelectedOrientation('portrait')}
              disabled={!availableVideos.portrait}
              title={!availableVideos.portrait ? 'Portrait video not available for this job' : 'Switch to portrait view'}
            >
              Portrait (720x1280) {!availableVideos.portrait && '❌'}
            </button>
            <button
              className={`${styles['orientation-button']} ${selectedOrientation === 'landscape' ? styles['active'] : ''} ${!availableVideos.landscape ? styles['disabled'] : ''}`}
              onClick={() => setSelectedOrientation('landscape')}
              disabled={!availableVideos.landscape}
              title={!availableVideos.landscape ? 'Landscape video not available for this job' : 'Switch to landscape view'}
            >
              Landscape (1280x720) {!availableVideos.landscape && '❌'}
            </button>
          </div>
        </div>

        {/* Final Video Preview */}
        <div className={styles['final-video-preview']}>
          <Typography variant="h5">Final Composition Preview</Typography>
          <div className={styles['video-container']}>
            {isLoadingVideo && <div className={styles['loading-spinner']}>Loading {selectedOrientation} video...</div>}
            {videoError && <div className={styles['video-error']}>{videoError}</div>}
            <video
              key={`${selectedOrientation}-${jobId}`}
              src={getFinalVideoUrl(selectedOrientation)}
              controls
              className={styles['composition-video']}
              poster={getFinalThumbnailUrl(selectedOrientation)}
              onLoadStart={() => setIsLoadingVideo(true)}
              onLoadedData={handleVideoLoad}
              onError={handleVideoError}
              preload="metadata"
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Final Thumbnail Preview */}
        <div className={styles['final-thumbnail-preview']}>
          <Typography variant="h6">Final Thumbnail</Typography>
          <img
            src={getFinalThumbnailUrl(selectedOrientation)}
            alt="Final composition thumbnail"
            className={styles['composition-thumbnail']}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
          <div className={styles['thumbnail-placeholder']}>No thumbnail available</div>
        </div>
      </div>

      <div className={styles['composition-details']}>
        {composition.duration && (
          <Typography variant="caption" color="secondary">Final Duration: {composition.duration} seconds</Typography>
        )}
        {composition.quality_score && (
          <Typography variant="caption" color="secondary">Quality Score: {composition.quality_score}/10</Typography>
        )}
        {composition.composition_notes && (
          <div className={styles['composition-notes']}>
            <Typography variant="h5">Composition Notes:</Typography>
            <Typography variant="body2">{composition.composition_notes}</Typography>
          </div>
        )}
        {composition.error && (
          <Typography variant="caption" color="secondary" className={styles['error-message']}>Error: {composition.error}</Typography>
        )}
        <Typography variant="caption" color="secondary">Last Updated: {new Date(composition.last_updated).toLocaleString()}</Typography>
      </div>
    </div>
  );
};

export default CompositionEditor;
